import { useCallback, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { COPILOT_OPEN_EVENT, COPILOT_PAGE } from "../../lib/copilot";
import useIsMobile from "../../hooks/useIsMobile";
import { useCopilotDock } from "../../hooks/useCopilotDock";
import { MobileSheet, MobileSheetContent, MobileSheetTitle } from "../ui/mobile-sheet";
import CopilotChat from "./CopilotChat";
import CopilotLogo from "./CopilotLogo";
import { useCopilotSession } from "./CopilotSession";

// The Copilote's floating bubble (bottom right, decision D16) and its panel. The conversation itself lives in CopilotSession
// (mounted once in Layout), shared with the full page: « Agrandir » carries the conversation over to /copilote, where the bubble
// is not shown. Computer: a window anchored bottom right that leaves the page usable. Phone: a full-height bottom sheet
// (DESIGN_SYSTEM § 23), the bubble sitting above the navigation bar.
const SWAP = "absolute inset-0 flex items-center justify-center transition duration-200 ease-out motion-reduce:transition-none";
const CLOSE_MS = 150; // the desktop panel's closing animation (keep in step with its animate-out duration)

export default function Copilot() {
  const session = useCopilotSession();
  const { pathname } = useLocation();
  if (!session) return null;
  return <CopilotWidget session={session} onPage={pathname === COPILOT_PAGE} />;
}

function CopilotWidget({ session, onPage }) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const hasBottomNav = useIsMobile(1023);
  const dock = useCopilotDock();
  const { state, actions, inputRef, waitingCard } = session;
  const { streaming } = state;
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false); // desktop panel playing its closing animation before it unmounts
  const bubbleRef = useRef(null);
  const { refreshAvailability, focusInput } = actions;

  const show = useCallback(() => {
    if (onPage) {
      focusInput(); // on the page, the question field is already there
      return;
    }
    setOpen(true);
  }, [onPage, focusInput]);
  const close = useCallback(() => {
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (isMobile || reduced) {
      setOpen(false);
    } else {
      setClosing(true);
      window.setTimeout(() => { setOpen(false); setClosing(false); }, CLOSE_MS);
    }
    window.setTimeout(() => bubbleRef.current?.focus(), 0);
  }, [isMobile]);

  useEffect(() => {
    if (onPage) setOpen(false);
  }, [onPage]);

  useEffect(() => {
    if (open) {
      refreshAvailability();
      focusInput();
    }
  }, [open, refreshAvailability, focusInput]);

  // Escape closes the non-modal desktop panel wherever the focus is (the page stays usable, so focus rarely sits inside it);
  // a dialog or menu that already handled the key keeps priority.
  useEffect(() => {
    if (!open || isMobile) return undefined;
    const onEscape = (event) => {
      if (event.key === "Escape" && !event.defaultPrevented) close();
    };
    window.addEventListener("keydown", onEscape);
    return () => window.removeEventListener("keydown", onEscape);
  }, [open, isMobile, close]);

  // Ctrl+J (⌘J on a Mac) opens or closes it; the search palette and the « Plus » menu open it through an event.
  useEffect(() => {
    const onKey = (event) => {
      if ((event.ctrlKey || event.metaKey) && !event.altKey && event.key.toLowerCase() === "j") {
        event.preventDefault();
        if (onPage) focusInput();
        else setOpen((value) => !value);
      }
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener(COPILOT_OPEN_EVENT, show);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener(COPILOT_OPEN_EVENT, show);
    };
  }, [show, onPage, focusInput]);

  // « Agrandir » : the same conversation, on its own page (or the page's start screen when none is under way). A view transition
  // morphs the panel into the page's conversation area (both carry `copilot-surface`, index.css) where the browser supports it;
  // elsewhere the page simply replaces the panel. The panel unmounts with the navigation itself (onPage), in the same frame.
  const expand = () => {
    const go = () => flushSync(() => navigate(COPILOT_PAGE));
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (!document.startViewTransition || reduced) navigate(COPILOT_PAGE);
    else document.startViewTransition(go);
  };

  if (onPage) return null;
  const hidden = isMobile && (dock.hiddenOnMobile || open);

  // Bottom right on a computer (above a fixed bar when a page shows one); above the navigation bar on a phone or a tablet,
  // at 80 % opacity on a phone so it hides less of the page.
  const position = hasBottomNav
    ? (dock.raised ? "bottom-[calc(9.5rem+env(safe-area-inset-bottom,0px))]" : "bottom-[calc(5rem+env(safe-area-inset-bottom,0px))]")
    : (dock.raised ? "bottom-24" : "bottom-6");

  // The window opens above the bubble; on a tablet that is above the bottom navigation too.
  const panelPosition = hasBottomNav
    ? (dock.raised ? "bottom-[13rem] h-[min(640px,calc(100vh-17rem))]" : "bottom-[8.5rem] h-[min(640px,calc(100vh-12.5rem))]")
    : `${dock.raised ? "bottom-40" : "bottom-24"} h-[min(640px,calc(100vh-8rem))]`;

  return (
    <>
      {!hidden && (
        <button ref={bubbleRef} type="button" onClick={() => (open ? close() : show())} aria-expanded={open && !closing} aria-controls="copilot-panel"
          aria-label={open ? "Fermer le Copilote Jamora" : `Ouvrir le Copilote Jamora${waitingCard ? " : une action attend votre réponse" : ""}`}
          title="Copilote Jamora (Ctrl+J)"
          className={`fixed right-4 md:right-6 ${position} z-40 flex h-12 w-12 items-center justify-center rounded-full border border-teal-500 bg-white opacity-80 shadow-lg transition hover:shadow-xl focus-visible:opacity-100 md:opacity-100 focus:outline-none focus-visible:ring-4 focus-visible:ring-jamora/30`}
          data-testid="copilot-bubble" data-thinking={streaming && !open ? "true" : "false"}>
          {/* Both stay mounted so the logo turns into the X (and back) instead of swapping abruptly. */}
          <span className={`${SWAP} ${open && !closing ? "rotate-90 scale-50 opacity-0" : "rotate-0 scale-100 opacity-100"}`} data-testid="copilot-bubble-logo">
            <CopilotLogo variant={streaming && !open ? "thinkingStrong" : "still"} size={30} />
          </span>
          <span className={`${SWAP} ${open && !closing ? "rotate-0 scale-100 opacity-100" : "-rotate-90 scale-50 opacity-0"}`} data-testid="copilot-bubble-close">
            <X size={22} className="text-jamora" aria-hidden="true" />
          </span>
          {/* An action waits for the person's answer: found again after closing the panel (proposal § 3 bis). */}
          {waitingCard && !open && (
            <span className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full border-2 border-white bg-amber-500" aria-hidden="true" data-testid="copilot-bubble-waiting" />
          )}
        </button>
      )}

      {open && !isMobile && (
        <div id="copilot-panel" role="dialog" aria-modal="false" aria-label="Copilote Jamora" data-testid="copilot-panel"
          style={{ viewTransitionName: "copilot-surface" }} data-state={closing ? "closed" : "open"}
          className={`fixed right-6 ${panelPosition} z-40 flex origin-bottom-right duration-200 ease-out motion-reduce:animate-none data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=open]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=closed]:slide-out-to-bottom-2 data-[state=closed]:duration-150 w-[480px] max-w-[calc(100vw-3rem)] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl`}>
          <CopilotChat state={state} actions={actions} onClose={close} onExpand={expand} inputRef={inputRef} showClose />
        </div>
      )}

      {isMobile && (
        <MobileSheet open={open} onOpenChange={(value) => (value ? show() : close())}>
          {/* The conversation takes the whole screen (user's choice, 2026-10-18), unlike the other sheets capped at 90 %. */}
          <MobileSheetContent aria-describedby={undefined}
            className="h-dvh max-h-none rounded-none border-t-0 bg-white pt-[env(safe-area-inset-top,0px)]" data-testid="copilot-sheet">
            <MobileSheetTitle className="sr-only">Copilote Jamora</MobileSheetTitle>
            <div id="copilot-panel" className="min-h-0 flex-1">
              <CopilotChat state={state} actions={actions} onClose={close} onNavigate={close} onExpand={expand} inputRef={inputRef} showClose />
            </div>
          </MobileSheetContent>
        </MobileSheet>
      )}
    </>
  );
}
